import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { History, CalendarClock, Eye, TrendingUp, TrendingDown, Minus, ChevronRight } from "lucide-react";
import MessagingChannelsPanel, { ReminderButtons } from "@/components/MessagingChannelsPanel";
import { toast } from "sonner";

/**
 * Colonna destra della cartella: Ultima visita, Prossimo appuntamento, Salute occhi OD/OS.
 */

// Estrae un intervallo temporale dal follow-up ("controllo tra 6 mesi", "tra 15 giorni", "tra 1 settimana"…)
// e restituisce la data suggerita YYYY-MM-DD, o null se non riconosciuto.
export function parseFollowupDate(text) {
  if (!text) return null;
  const t = String(text).toLowerCase();
  let n = null, unit = null;
  let m = t.match(/tra\s+(\d+)\s*(mes|settiman|giorn|ann)/);
  if (m) { n = parseInt(m[1], 10); unit = m[2]; }
  else {
    m = t.match(/tra\s+(?:un[oa]?|una)\s+(mese|settimana|giorno|anno)/);
    if (m) { n = 1; unit = m[1]; }
  }
  if (!n || n <= 0 || !unit) return null;
  const d = new Date();
  if (unit.startsWith("mes")) d.setMonth(d.getMonth() + n);
  else if (unit.startsWith("sett")) d.setDate(d.getDate() + n * 7);
  else if (unit.startsWith("gio")) d.setDate(d.getDate() + n);
  else if (unit.startsWith("ann")) d.setFullYear(d.getFullYear() + n);
  else return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_STYLE = {
  verde: { bg: "bg-emerald-50 border-emerald-300", dot: "bg-emerald-500", label: "In salute / stabile", text: "text-emerald-800" },
  arancione: { bg: "bg-amber-50 border-amber-300", dot: "bg-amber-500", label: "Da monitorare", text: "text-amber-800" },
  rosso: { bg: "bg-red-50 border-red-300", dot: "bg-red-500", label: "Criticità", text: "text-red-800" },
  insufficiente: { bg: "bg-slate-50 border-slate-200", dot: "bg-slate-300", label: "Dati insufficienti", text: "text-slate-500" },
};

function Sparkline({ series }) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const vals = series.map((s) => s.visus);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const W = 90, H = 26;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${H - ((v - min) / (max - min || 1)) * (H - 4) - 2}`).join(" ");
  return (
    <svg width={W} height={H} className="shrink-0" data-testid="eye-health-sparkline">
      <polyline points={pts} fill="none" stroke="#2C6E91" strokeWidth="1.6" />
    </svg>
  );
}

function EyeCard({ side, data, onOverride, readOnly }) {
  const st = STATUS_STYLE[data?.status] || STATUS_STYLE.insufficiente;
  const TrendIcon = data?.trend === "miglioramento" ? TrendingUp : data?.trend === "peggioramento" ? TrendingDown : Minus;
  return (
    <div className={`border rounded-lg p-2.5 ${st.bg}`} data-testid={`eye-health-${side.toLowerCase()}`}>
      <div className="flex items-center gap-1.5">
        <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`}></span>
        <span className="font-extrabold text-xs text-brand-navy">{side}</span>
        <span className={`text-[10px] font-bold ${st.text}`}>{st.label}</span>
        {data?.override && <span className="text-[8px] uppercase bg-slate-200 text-slate-600 px-1 rounded font-bold">medico</span>}
      </div>
      {data?.trend && (
        <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-600">
          <TrendIcon size={11} /> {data.trend}
          {data?.latest_visus != null && <span className="ml-1 font-mono">visus {data.latest_visus}</span>}
        </div>
      )}
      <Sparkline series={data?.series} />
      {(data?.reasons || []).slice(0, 2).map((r, i) => (
        <div key={i} className="text-[9px] text-slate-500 leading-tight mt-0.5">{r}</div>
      ))}
      {!readOnly && (
        <select value="" data-testid={`eye-health-override-${side.toLowerCase()}`}
          onChange={(e) => e.target.value && onOverride(side, e.target.value)}
          className="mt-1.5 w-full text-[10px] px-1.5 py-1 rounded border border-slate-300 bg-white text-slate-600">
          <option value="">Correggi stato…</option>
          <option value="verde">Verde — in salute</option>
          <option value="arancione">Arancione — monitorare</option>
          <option value="rosso">Rosso — criticità</option>
        </select>
      )}
    </div>
  );
}

export default function VisitSidebar({ patient, phone, email, history = [], onOpenPreview, refreshKey, readOnly, followupText = "", sourceVisitId = null }) {
  const nav = useNavigate();
  const [nextAppt, setNextAppt] = useState(null);
  const [eyeHealth, setEyeHealth] = useState(null);
  const [showAllVisits, setShowAllVisits] = useState(false);
  const [filterService, setFilterService] = useState("");
  const suggestedDate = parseFollowupDate(followupText);

  const params = {
    fiscal_code: patient?.fiscal_code || undefined,
    name: patient?.name || undefined,
    surname: patient?.surname || undefined,
    birth: patient?.birth || undefined,
  };

  useEffect(() => {
    if (!patient?.name && !patient?.fiscal_code) return;
    (async () => {
      try { const { data } = await api.get("/medico/patient-next-appointment", { params }); setNextAppt(data); } catch { setNextAppt(null); }
      try { const { data } = await api.get("/medico/patient-eye-health", { params }); setEyeHealth(data); } catch { setEyeHealth(null); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.fiscal_code, patient?.name, patient?.surname, refreshKey]);

  const override = async (side, status) => {
    try {
      await api.put("/medico/patient-eye-health", {
        patient_fiscal_code: params.fiscal_code, patient_name: params.name,
        patient_surname: params.surname, patient_birth: params.birth,
        [side]: { status },
      });
      const { data } = await api.get("/medico/patient-eye-health", { params });
      setEyeHealth(data);
      toast.success(`Stato ${side} aggiornato dal medico`);
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  const last = history[0];
  const filteredHistory = history.filter((h) => !filterService || (h.service || "") === filterService);
  const services = Array.from(new Set(history.map((h) => h.service).filter(Boolean)));

  return (
    <div className="space-y-3" data-testid="visit-sidebar">
      {/* 1. ULTIMA VISITA */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid="sidebar-last-visit">
        <div className="flex items-center gap-1.5 mb-2">
          <History size={13} className="text-brand-azure" />
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Ultima visita</span>
        </div>
        {last ? (
          <>
            <button data-testid="sidebar-last-visit-date" onClick={() => onOpenPreview(last.id)}
              className="text-sm font-extrabold text-brand-azure hover:underline">
              {new Date(last.created_at).toLocaleDateString("it-IT")}
            </button>
            <div className="text-[11px] text-slate-600 mt-0.5">{(last.service || "").replace(/_/g, " ")}</div>
            {last.diagnosi && <div className="text-[11px] text-slate-700 mt-1 line-clamp-2"><b>Dx:</b> {last.diagnosi}</div>}
            <div className="flex flex-col gap-1 mt-2">
              <button data-testid="sidebar-preview-btn" onClick={() => onOpenPreview(last.id)}
                className="text-[11px] font-bold text-white bg-brand-azure hover:bg-brand-navy rounded-lg px-2.5 py-1.5 text-center">
                Visualizza anteprima
              </button>
              <button data-testid="sidebar-open-last-visit" onClick={() => nav(`/visite/${last.id}`)}
                className="text-[11px] font-bold text-white bg-brand-navy hover:bg-brand-navyDark rounded-lg px-2.5 py-1.5 text-center">
                Apri visita completa
              </button>
              <button data-testid="sidebar-all-visits" onClick={() => setShowAllVisits((x) => !x)}
                className="text-[11px] font-bold text-brand-azure hover:bg-brand-azure/10 rounded-lg px-2.5 py-1.5 flex items-center justify-center gap-1">
                Tutte le visite pregresse ({history.length}) <ChevronRight size={11} className={showAllVisits ? "rotate-90" : ""} />
              </button>
            </div>
            {showAllVisits && (
              <div className="mt-2 border-t border-slate-100 pt-2" data-testid="sidebar-visits-list">
                {services.length > 1 && (
                  <select value={filterService} onChange={(e) => setFilterService(e.target.value)}
                    className="w-full mb-1.5 text-[10px] px-1.5 py-1 rounded border border-[#E2E8F0] bg-white">
                    <option value="">Tutti i tipi</option>
                    {services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredHistory.map((h) => (
                    <button key={h.id} onClick={() => onOpenPreview(h.id)} data-testid={`sidebar-visit-${h.id}`}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 border border-transparent hover:border-[#E2E8F0]">
                      <div className="text-[10px] text-slate-500">{new Date(h.created_at).toLocaleDateString("it-IT")} · {(h.service || "").toUpperCase()}</div>
                      <div className="text-[11px] font-semibold text-brand-navy truncate">{h.diagnosi || (h.status === "closed" ? "Visita chiusa" : "Bozza")}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-[11px] text-slate-400 italic">Nessuna visita pregressa</div>
        )}
      </div>

      {/* 2. PROSSIMO APPUNTAMENTO */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid="sidebar-next-appointment">
        <div className="flex items-center gap-1.5 mb-2">
          <CalendarClock size={13} className="text-brand-orange" />
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Prossimo appuntamento</span>
        </div>
        {nextAppt?.found ? (
          <>
          <button
            onClick={() => nav(`/agenda?date=${(nextAppt.appointment.starts_at || "").slice(0, 10)}&appt=${nextAppt.appointment.id}`)}
            className="w-full text-left" data-testid="sidebar-appt-info">
            <div className="text-sm font-extrabold text-brand-navy">
              {new Date(nextAppt.appointment.starts_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              {(nextAppt.appointment.type || nextAppt.appointment.service || "Visita")} · {nextAppt.appointment.status || "programmato"}
            </div>
            <div className="text-[10px] text-brand-azure font-bold mt-1">Apri in agenda per modificare →</div>
          </button>
          <ReminderButtons
            patient={patient} phone={phone} email={email} eventType="appointment" compact
            context={{
              data: new Date(nextAppt.appointment.starts_at).toLocaleDateString("it-IT"),
              ora: new Date(nextAppt.appointment.starts_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
            }}
          />
          </>
        ) : (
          <>
          <button data-testid="sidebar-appt-create"
            onClick={() => {
              try {
                window.localStorage.setItem("pv-appt-prefill", JSON.stringify({
                  patient_name: patient?.name || "",
                  patient_surname: patient?.surname || "",
                  patient_birth: patient?.birth || "",
                  patient_fiscal_code: patient?.fiscal_code || "",
                  patient_phone: phone || "",
                  patient_email: email || "",
                }));
              } catch { /* noop */ }
              const qs = new URLSearchParams({ prefill: "1" });
              if (suggestedDate) qs.set("date", suggestedDate);
              if (sourceVisitId) qs.set("sourceVisitId", sourceVisitId);
              nav(`/agenda?${qs.toString()}`);
            }}
            className="w-full text-[11px] font-bold text-white bg-brand-orange hover:bg-brand-orangeDark rounded-lg px-2.5 py-2">
            Programma prossimo appuntamento
          </button>
          {suggestedDate && (
            <div className="text-[10px] text-slate-500 mt-1.5" data-testid="sidebar-appt-suggested-date">
              Data suggerita dal follow-up: <b className="text-brand-navy">{new Date(`${suggestedDate}T12:00:00`).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</b> — confermi tu in agenda.
            </div>
          )}
          </>
        )}
      </div>

      {/* 3. SALUTE OCCHI */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid="sidebar-eye-health">
        <div className="flex items-center gap-1.5 mb-2">
          <Eye size={13} className="text-brand-navy" />
          <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Salute occhi</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <EyeCard side="OD" data={eyeHealth?.OD} onOverride={override} />
          <EyeCard side="OS" data={eyeHealth?.OS} onOverride={override} />
        </div>
        <div className="text-[9px] text-slate-400 mt-1.5 leading-tight">
          Stima automatica da visus, tono e diagnosi delle visite chiuse. Il medico può confermare o correggere.
        </div>
      </div>

      {/* 4. CANALI COMUNICAZIONE + CONSENSI */}
      <MessagingChannelsPanel patient={patient} phone={phone} email={email} readOnly={readOnly} />
    </div>
  );
}
